import * as React from 'react';

import { Button, ButtonProps } from './Button';
import { Input } from './Input';
import { Textarea } from './Textarea';
import { VariantProps, cn, tv } from './utils/cn';

/**
 * Container that pairs an `<Input>` (or `<Textarea>`) with one or more
 * `<InputGroupAddon>` elements. The container owns the border, focus ring,
 * `aria-invalid` styling, and disabled state; inner controls render
 * borderless and transparent so the visual chrome lives in one place.
 *
 * Composition is driven by `data-align` on each addon, which the parent
 * matches with `has-[>[data-align=...]]` selectors to switch the row
 * direction (`inline-*` keeps the row, `block-*` flips to column).
 *
 * @example
 * <InputGroup>
 *   <InputGroupAddon align="inline-start">
 *     <Icon name="Search" />
 *   </InputGroupAddon>
 *   <InputGroupInput placeholder="Search patients" />
 * </InputGroup>
 */
function InputGroup({
  className,
  size = 'md',
  ...props
}: React.ComponentProps<'div'> & { size?: 'md' | 'lg' }) {
  return (
    <div
      data-slot="input-group"
      data-size={size}
      role="group"
      className={cn(
        'group/input-group relative flex w-full min-w-0 items-center border border-input transition-colors outline-none',
        size === 'lg' ? 'h-12 gap-3 rounded-lg' : 'h-10 gap-2.5 rounded-md',
        'in-data-[slot=combobox-content]:focus-within:border-inherit in-data-[slot=combobox-content]:focus-within:ring-0',
        'has-disabled:bg-input/50 has-disabled:opacity-50',
        'has-[[data-slot=input-group-control]:focus-visible]:border-ring has-[[data-slot=input-group-control]:focus-visible]:ring-3 has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50',
        'has-[[data-slot][aria-invalid=true]]:border-destructive has-[[data-slot][aria-invalid=true]]:ring-3 has-[[data-slot][aria-invalid=true]]:ring-destructive/20',
        'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>textarea]:h-auto',
        'dark:bg-input/30 dark:has-disabled:bg-input/80 dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40',
        'has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=block-start]]:[&>input]:pb-3 has-[>[data-align=inline-end]]:[&>input]:pr-1.5 has-[>[data-align=inline-start]]:[&>input]:pl-1.5',
        className,
      )}
      {...props}
    />
  );
}

const inputGroupAddonStyles = tv({
  base: "flex h-auto cursor-text items-center justify-center gap-2 py-1.5 text-sm font-medium text-muted-foreground select-none group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-sm [&>svg:not([class*='size-'])]:size-4",
  variants: {
    align: {
      'inline-start':
        'order-first pl-2.5 group-data-[size=lg]/input-group:pl-4 has-[>button]:ml-[-0.3rem] has-[>kbd]:ml-[-0.15rem]',
      'inline-end':
        'order-last pr-2.5 group-data-[size=lg]/input-group:pr-4 has-[>button]:mr-[-0.3rem] has-[>kbd]:mr-[-0.15rem]',
      'block-start':
        'order-first w-full justify-start border-b border-border px-2.5 py-2 group-has-[>input]/input-group:pt-2 group-data-[size=lg]/input-group:px-4',
      'block-end':
        'order-last w-full justify-start border-t border-border px-2.5 py-2 group-has-[>input]/input-group:pb-2 group-data-[size=lg]/input-group:px-4',
    },
  },
  defaultVariants: {
    align: 'inline-start',
  },
});

/**
 * Props for the {@link InputGroupAddon} component. Combines the `align`
 * variant with standard `<div>` attributes (`ref`, `onClick`, `data-*`,
 * `aria-*`, etc.), which forward to the underlying element.
 */
export type InputGroupAddonProps = React.ComponentProps<'div'> &
  VariantProps<typeof inputGroupAddonStyles>;

/**
 * Prefix or suffix slot inside an {@link InputGroup}. Pick `align` to choose
 * placement: `inline-start` and `inline-end` flank the input on the same row;
 * `block-start` and `block-end` stack above or below it (the parent flips to
 * column layout when a block-aligned addon is present).
 *
 * Clicking the addon body focuses the sibling `<input>`. Clicking a nested
 * button inside the addon does not.
 *
 * @example
 * <InputGroupAddon align="inline-end">
 *   <Kbd>⌘K</Kbd>
 * </InputGroupAddon>
 */
function InputGroupAddon({
  className,
  align = 'inline-start',
  ...props
}: InputGroupAddonProps) {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={inputGroupAddonStyles({ align, className })}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) {
          return;
        }
        e.currentTarget.parentElement?.querySelector('input')?.focus();
      }}
      {...props}
    />
  );
}

const inputGroupButtonStyles = tv({
  base: 'shadow-none',
  variants: {
    size: {
      xs: 'h-6 rounded-sm px-1.5',
      sm: '-mr-0.5 h-7 rounded-md px-2.5',
    },
  },
  defaultVariants: {
    size: 'xs',
  },
});

/**
 * Props for the {@link InputGroupButton} component. Forwards every
 * {@link Button} prop except `size`, which is replaced by InputGroup's
 * tighter scale (`xs` | `sm`) so the inner button fits the parent's height.
 */
export type InputGroupButtonProps = Omit<ButtonProps, 'size'> &
  VariantProps<typeof inputGroupButtonStyles>;

/**
 * Compact `<Button>` sized to fit inside an {@link InputGroup}. Defaults to
 * `variant="ghost"` and `size="xs"`. Pass an `<Icon>` as the only child for a
 * square icon-only button: {@link Button}'s auto-detection takes care of the
 * aspect ratio.
 *
 * Built on top of {@link Button}, so all the usual button props
 * (`onClick`, `disabled`, `aria-*`, `type`) work as you'd expect.
 */
function InputGroupButton({
  className,
  type = 'button',
  variant = 'ghost',
  size = 'xs',
  ...props
}: InputGroupButtonProps) {
  return (
    <Button
      type={type}
      size={size}
      variant={variant}
      className={inputGroupButtonStyles({ size, className })}
      {...props}
    />
  );
}

/**
 * Inline text inside an {@link InputGroupAddon}. Renders as a `<span>` with
 * muted color and the standard 2-unit gap, matching the rest of the addon
 * affordances.
 *
 * @example
 * <InputGroupAddon align="inline-start">
 *   <InputGroupText>$</InputGroupText>
 * </InputGroupAddon>
 */
function InputGroupText({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      className={cn(
        "flex items-center gap-2 text-sm text-muted-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

/**
 * The text input slot inside an {@link InputGroup}. Wraps the project's
 * {@link Input} with `data-slot="input-group-control"` (the marker the parent
 * uses for focus/invalid styling) and overrides the inner Input's border,
 * background, shadow, and ring so all visual chrome lives on the parent.
 */
function InputGroupInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        'h-auto flex-1 rounded-none border-0 bg-transparent shadow-none ring-0 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0 dark:bg-transparent dark:disabled:bg-transparent',
        className,
      )}
      {...props}
    />
  );
}

/**
 * The textarea slot inside an {@link InputGroup}. Wraps the project's
 * {@link Textarea} with `data-slot="input-group-control"` and the same
 * chrome-stripping overrides as {@link InputGroupInput}, plus
 * `resize-none` since the parent grows around the textarea.
 */
function InputGroupTextarea({
  className,
  ...props
}: React.ComponentProps<typeof Textarea>) {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn(
        'flex-1 resize-none rounded-none border-0 bg-transparent py-2 shadow-none ring-0 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0 dark:bg-transparent dark:disabled:bg-transparent',
        className,
      )}
      {...props}
    />
  );
}

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
};
